import { Link } from "react-router-dom";
import { Sun, ArrowRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { AccountMenu } from "@/components/layout/AccountMenu";
import { useAccount } from "@/hooks/useAuth";
import ouVaLelecImg from "@/assets/illustrations/ou-va-lelec.png";

/**
 * Section Hero de la landing (S7-5, GAP-3 ; copy V1 refondue S16-3).
 *
 * Contient le **seul `<h1>`** de la page (contrainte a11y S7-1 § 1.3 :
 * une seule racine `h1` par document, les autres sections sont en `h2`).
 *
 * Accroche S16-3 (décision PO 2026-06-12, repositionnée) : centrée sur le
 * **bon dimensionnement** de l'installation (panneaux + batterie) **selon les
 * objectifs** du client. L'économie (ROI) n'est qu'un critère de choix parmi
 * d'autres (ROI / autoconso / autoproduction). Parcours anonyme « sans
 * inscription » conservé.
 *
 * Mini-header light intégré :
 * - Logo SunMatch (Sun lucide + libellé + badge bêta).
 * - Lien `Politique de confidentialité` (obligation RGPD art. 13, doit
 *   rester accessible publiquement avant tout consentement).
 *
 * Le CTA principal pointe directement sur `/simulations/new` (le wizard
 * gère lui-même la modale de consentement via `AppLayout`). Vocabulaire de
 * nav figé en S16-2 : « Nouvelle simulation », « Se connecter » (`/login`).
 */
export function LandingHero(): JSX.Element {
  const { data: account, isLoading } = useAccount();
  return (
    <header>
      {/* Barre du haut blanche — uniformisée avec la Navbar des autres pages. */}
      <div className="border-b border-slate-200 bg-white">
        {/* Toujours en ligne (logo ↔ avatar/connexion) à toutes les largeurs,
            comme la `Navbar` des autres pages : sans ça, `flex-col` sous le
            breakpoint `sm` faisait passer l'avatar à la ligne sous le logo. */}
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-2 px-4 py-4">
          <Link
            to="/"
            className="flex items-center gap-2 text-lg font-semibold"
            aria-label="SunMatch — accueil"
          >
            <Sun className="h-5 w-5 text-amber-500" aria-hidden />
            <span>SunMatch</span>
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
              bêta
            </span>
          </Link>
          <div className="flex items-center gap-3">
            {/* Connecté → avatar (comme dans l'app) ; sinon « Se connecter ».
                Le lien « Politique de confidentialité » a été retiré (déjà en
                pied de page). */}
            {isLoading ? null : account ? (
              <AccountMenu email={account.email} isAdmin={account.is_admin} />
            ) : (
              <Button asChild variant="outline" size="sm">
                <Link to="/login">Se connecter</Link>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Contenu hero — dégradé ambre. */}
      <div className="bg-gradient-to-b from-amber-50 to-white">
      <div className="mx-auto grid max-w-6xl gap-10 px-4 py-8 sm:py-10 lg:grid-cols-2 lg:items-center lg:py-12">
        <div className="space-y-6">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            Adaptez la puissance de votre installation solaire{" "}
            {/* H7-2 : amber-600 (~3.45:1 sur fond clair) → amber-700 (~4.85:1)
                pour respecter WCAG AA même quand le span est cassé sur une ligne
                qui ne déclenche pas le seuil « large text » (3:1). */}
            <span className="text-amber-700">à vos besoins</span>
          </h1>
          <p className="text-lg leading-relaxed text-slate-700 sm:text-xl">
            Avant d'investir dans le solaire, une seule question compte : quelle
            installation vous faut-il vraiment ? SunMatch vous aide, en toute
            indépendance, à la dimensionner à partir de vos données et selon vos
            objectifs.
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link
                to="/simulations/new"
                className="inline-flex items-center justify-center gap-2"
              >
                Lancer une simulation
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </Button>
          </div>
        </div>

        {/* Aperçu produit : exemple de résultat SunMatch (répartition horaire des
            flux d'énergie). Masqué sous `lg` pour ne pas alourdir le mobile. */}
        <div className="relative hidden items-center justify-center lg:flex">
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-amber-200 via-amber-100 to-white opacity-80" />
          <img
            src={ouVaLelecImg}
            alt="Exemple de résultat SunMatch : répartition heure par heure de l'électricité produite par les panneaux (autoconsommation directe, recharge de la batterie, énergie revendue et surplus perdu), avec le niveau de charge de la batterie."
            className="relative w-full rounded-3xl border border-amber-200 bg-white/80 p-4 shadow-sm backdrop-blur"
          />
        </div>
      </div>
      </div>
    </header>
  );
}
